import React, { useRef, useState } from 'react';
import { Link2, UploadCloud, Video } from 'lucide-react';
import { ACCEPTED_VIDEO_TYPES, MAX_VIDEO_BYTES } from '../../lib/reelCreatorConfig';

export default function ReferenceUpload({ onSubmit, busy }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [reelUrl, setReelUrl] = useState('');
  const [error, setError] = useState('');
  const chooseFile = (nextFile) => {
    setError('');
    if (!nextFile) return;
    if (!ACCEPTED_VIDEO_TYPES.includes(nextFile.type)) return setError('Please choose an MP4, MOV, or WebM video.');
    if (nextFile.size > MAX_VIDEO_BYTES) return setError('Please choose a video smaller than 250 MB.');
    setFile(nextFile);
    setReelUrl('');
  };
  const submit = (event) => {
    event.preventDefault();
    if (!file && !reelUrl.trim()) return setError('Paste a public Reel link or choose a video.');
    onSubmit({ file, reelUrl: reelUrl.trim() });
  };
  return (
    <form onSubmit={submit} className="mx-auto max-w-xl">
      <div className="text-center">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-purple-300">Step 1</p>
        <h2 className="mt-2 text-3xl font-bold text-white">Add a Reference Reel</h2>
        <p className="mt-2 text-sm text-gray-400">Paste a public Instagram or TikTok link and we will import the video automatically, or choose a saved video.</p>
      </div>
      <label className="mt-7 block text-sm font-semibold text-white">Upload Video</label>
      <button type="button" onClick={() => inputRef.current?.click()} onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); chooseFile(event.dataTransfer.files?.[0]); }} className="mt-2 flex min-h-44 w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed border-purple-500/50 bg-purple-500/5 p-6 text-center hover:border-purple-400 hover:bg-purple-500/10 focus:outline-none focus:ring-2 focus:ring-purple-300">
        {file ? <Video className="text-green-300" size={34} /> : <UploadCloud className="text-purple-300" size={34} />}
        <span className="mt-3 font-semibold text-white">{file ? file.name : 'Tap to choose a video'}</span>
        <span className="mt-1 text-xs text-gray-500">MP4, MOV, or WebM · up to 250 MB</span>
      </button>
      <input ref={inputRef} className="sr-only" type="file" accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm" onChange={(event) => chooseFile(event.target.files?.[0])} />
      <div className="my-5 flex items-center gap-3 text-xs text-gray-600"><span className="h-px flex-1 bg-gray-800" />OR<span className="h-px flex-1 bg-gray-800" /></div>
      <label htmlFor="reel-link" className="block text-sm font-semibold text-white">Paste Reel Link</label>
      <div className="relative mt-2">
        <Link2 className="absolute left-3 top-3.5 text-gray-500" size={18} />
        <input id="reel-link" type="url" inputMode="url" value={reelUrl} onChange={(event) => { setReelUrl(event.target.value); if (event.target.value) setFile(null); }} placeholder="Paste Instagram or TikTok Reel link" className="min-h-12 w-full rounded-xl border border-gray-700 bg-gray-900 py-3 pl-10 pr-3 text-base text-white placeholder:text-gray-600 focus:border-purple-400 focus:outline-none focus:ring-1 focus:ring-purple-400" />
      </div>
      <p className="mt-2 text-xs leading-5 text-gray-500">The link must be public and viewable without signing in. Private, deleted, age-restricted or region-blocked Reels cannot be imported.</p>
      {error && <p role="alert" className="mt-3 text-sm text-red-300">{error}</p>}
      <button type="submit" disabled={busy} className="mt-6 min-h-14 w-full rounded-xl bg-purple-600 px-5 py-3 font-bold text-white hover:bg-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-300 disabled:opacity-50">
        {busy ? (reelUrl ? 'IMPORTING REEL…' : 'UPLOADING…') : 'ANALYSE REEL'}
      </button>
    </form>
  );
}
